import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
  getOrganizations,
  getAssessments,
  getAssessmentLinks,
} from '../../utils/api';
import {
  Building2,
  ClipboardList,
  Link as LinkIcon,
  Users,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({
    organizations: 0,
    assessments: 0,
    completedAssessments: 0,
    activeLinks: 0,
  });
  const [recentAssessments, setRecentAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [orgsRes, assessmentsRes, linksRes] = await Promise.all([
        getOrganizations(),
        getAssessments({ limit: 10 }),
        getAssessmentLinks(),
      ]);

      const completedAssessments = assessmentsRes.data.filter(
        (a) => a.status === 'completed'
      ).length;

      const activeLinks = linksRes.data.filter((l) => l.is_active).length;

      setStats({
        organizations: orgsRes.data.length,
        assessments: assessmentsRes.data.length,
        completedAssessments,
        activeLinks,
      });

      setRecentAssessments(assessmentsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Organizations',
      value: stats.organizations,
      icon: Building2,
      color: 'bg-blue-500',
      link: '/admin/organizations',
    },
    {
      name: 'Total Assessments',
      value: stats.assessments,
      icon: ClipboardList,
      color: 'bg-green-500',
      link: '/admin/assessments',
    },
    {
      name: 'Completed',
      value: stats.completedAssessments,
      icon: TrendingUp,
      color: 'bg-tsvmap-orange',
      link: '/admin/assessments?status=completed',
    },
    {
      name: 'Active Links',
      value: stats.activeLinks,
      icon: LinkIcon,
      color: 'bg-purple-500',
      link: '/admin/links',
    },
  ];

  const styleColors = {
    D: 'bg-red-100 text-red-800',
    I: 'bg-orange-100 text-orange-800',
    S: 'bg-blue-100 text-blue-800',
    C: 'bg-yellow-100 text-yellow-800',
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your DISC assessment platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Assessments */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Assessments</h2>
          <Link
            to="/admin/assessments"
            className="text-tsvmap-orange hover:text-tsvmap-orange-dark text-sm font-medium flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        {recentAssessments.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {recentAssessments.map((assessment) => (
              <div key={assessment.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {assessment.respondent_first_name} {assessment.respondent_last_name}
                      </span>
                      {assessment.primary_style && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            styleColors[assessment.primary_style]
                          }`}
                        >
                          {assessment.style_blend || assessment.primary_style}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {assessment.organization_name || 'No organization'}
                      {assessment.job_role_title && ` • ${assessment.job_role_title}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        assessment.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : assessment.status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {assessment.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(assessment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No assessments yet</p>
            <p className="text-sm mt-1">Create an assessment link to get started</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/organizations"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow border-l-4 border-blue-500"
        >
          <Building2 className="h-8 w-8 text-blue-500 mb-3" />
          <h3 className="font-semibold text-gray-900">Add Organization</h3>
          <p className="text-sm text-gray-600 mt-1">
            Create a new customer or organization
          </p>
        </Link>

        <Link
          to="/admin/job-roles"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow border-l-4 border-green-500"
        >
          <Users className="h-8 w-8 text-green-500 mb-3" />
          <h3 className="font-semibold text-gray-900">Create Job Role</h3>
          <p className="text-sm text-gray-600 mt-1">
            Define ideal DISC profiles for positions
          </p>
        </Link>

        <Link
          to="/admin/links"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow border-l-4 border-tsvmap-orange"
        >
          <LinkIcon className="h-8 w-8 text-tsvmap-orange mb-3" />
          <h3 className="font-semibold text-gray-900">Create Assessment Link</h3>
          <p className="text-sm text-gray-600 mt-1">
            Generate a shareable link for assessments
          </p>
        </Link>
      </div>
    </Layout>
  );
};

export default Dashboard;
